/**
 * Logs tab for a gateway or connector (design 19 §7.2 "Logs tab", §7.3).
 *
 * Reads `{job="access-logs", node="$node"}` through the tenant-guarded proxy.
 * The `node` matcher here NARROWS; the proxy still injects the caller's node
 * allow-list into the same selector, so naming another tenant's node returns
 * nothing rather than that node's journal (design §6.3).
 *
 * Level facets are computed from the returned lines rather than from a Loki
 * label-values call: the counts then describe *this* window, which is the
 * question an operator is actually asking ("how many errors in the last hour"),
 * and it costs no extra request.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { observabilityService } from '../../services/observability.service';
import { resolveTimeRange, type TimeRangeValue } from '../../utils/timeRange';

interface LogRow {
  ts: string;
  line: string;
  level: string;
  unit: string;
}

const LEVELS = ['all', 'error', 'warn', 'info', 'debug'] as const;
type Level = (typeof LEVELS)[number];

const LEVEL_CLASS: Record<string, string> = {
  error: 'text-danger-600',
  warn: 'text-warning-600',
  info: 'text-gray-600',
  debug: 'text-gray-400',
};

/** Loki escaping for a value we put inside a `"` matcher or a line filter. */
function esc(v: string): string {
  return v.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export const NodeLogsPanel = ({
  node,
  range,
  limit = 200,
}: {
  node: string;
  range: TimeRangeValue;
  limit?: number;
}) => {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState<Level>('all');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');

  const query = useMemo(() => {
    const selector = `{job="access-logs", node="${esc(node)}"${level === 'all' ? '' : `, level="${level}"`}}`;
    return applied ? `${selector} |= "${esc(applied)}"` : selector;
  }, [node, level, applied]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { start, end } = resolveTimeRange(range);
      const res = await observabilityService.queryLogsRange(query, start, end, limit);
      const out: LogRow[] = [];
      for (const stream of res?.data?.result ?? []) {
        for (const [ns, line] of stream.values ?? []) {
          out.push({
            ts: new Date(Number(BigInt(ns) / 1000000n)).toISOString(),
            line,
            level: stream.stream.level ?? stream.stream.slog_level?.toLowerCase() ?? 'info',
            unit: stream.stream.unit ?? '',
          });
        }
      }
      out.sort((a, b) => (a.ts < b.ts ? 1 : -1));
      setRows(out);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to query logs');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [query, range, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  const facets = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of rows) counts[r.level] = (counts[r.level] ?? 0) + 1;
    return counts;
  }, [rows]);

  return (
    <div className="card" data-testid="node-logs-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="mr-auto text-sm font-semibold text-gray-900">
          System logs <span className="font-mono text-xs font-normal text-gray-400">{node}</span>
        </h3>
        {LEVELS.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLevel(l)}
            data-testid={`log-level-${l}`}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
              level === l ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300'
            }`}
          >
            {l}
            {l !== 'all' && facets[l] !== undefined && (
              <span className={`ml-1 ${level === l ? 'text-white/80' : 'text-gray-400'}`}>{facets[l]}</span>
            )}
          </button>
        ))}
        <input
          className="input-base w-48 text-xs"
          placeholder="Contains…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setApplied(search);
          }}
          aria-label="Log line filter"
        />
        <button type="button" className="btn-secondary px-2 py-1" onClick={() => void load()} title="Refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <p className="mb-2 font-mono text-[11px] text-gray-400" data-testid="node-logs-query">
        {query}
      </p>

      {error && <div className="mb-2 rounded border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700">{error}</div>}

      <div className="max-h-[28rem] overflow-auto rounded border border-gray-200 bg-gray-50" data-testid="node-logs-lines">
        {loading ? (
          <p className="p-4 text-sm text-gray-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-gray-400">No log lines in this window.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((r, i) => (
              <li key={`${r.ts}-${i}`} className="px-3 py-1.5 font-mono text-[11px] leading-relaxed">
                <span className="mr-2 text-gray-400">{r.ts.replace('T', ' ').slice(0, 23)}</span>
                <span className={`mr-2 uppercase ${LEVEL_CLASS[r.level] ?? 'text-gray-500'}`}>{r.level}</span>
                {r.unit && <span className="mr-2 text-gray-400">{r.unit}</span>}
                <span className="break-all text-gray-800">{r.line}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {!loading && rows.length > 0 && (
        <p className="mt-2 text-xs text-gray-400">
          {rows.length} line{rows.length === 1 ? '' : 's'} (newest first, capped at {limit})
        </p>
      )}
    </div>
  );
};

export default NodeLogsPanel;
